import React from 'react';
import YouTube from 'react-youtube';
import './YoutubePlayer.scss';

interface Props {
  url: string;
}

/**
 * 从各种 YouTube 链接中提取 11 位视频 ID
 *
 * 目前支持：
 *  1. https://youtu.be/<id> 
 *  2. https://www.youtube.com/watch?v=<id>
 *  3. https://www.youtube.com/embed/<id>
 *  4. https://www.youtube.com/v/<id>
 *  5. https://www.youtube.com/shorts/<id>
 *  6. https://www.youtube.com/live/<id>
 *  7. https://www.youtube-nocookie.com/embed/<id>
 *
 * 如后面还有其它新格式，只需在 YT_REGEX_LIST 中再加一条即可。
 */
const getYoutubeId = (url: string): string | null => {
  if (!url) return null;

  // YouTube 视频 ID 固定 11 位
  const ID_LEN = 11;

  // 针对不同路径的简易正则
  const YT_REGEX_LIST: RegExp[] = [
    /youtu\.be\/([^#&?]{11})/,                                  // youtu.be/<id>
    /youtube(?:-nocookie)?\.com\/watch\?.*v=([^#&?]{11})/,      // ...watch?v=<id>
    /youtube(?:-nocookie)?\.com\/embed\/([^#&?]{11})/,          // .../embed/<id>
    /youtube(?:-nocookie)?\.com\/v\/([^#&?]{11})/,              // .../v/<id>
    /youtube(?:-nocookie)?\.com\/shorts\/([^#&?]{11})/,         // .../shorts/<id>
    /youtube(?:-nocookie)?\.com\/live\/([^#&?]{11})/,           // .../live/<id>
  ];

  // 先用正则尝试快速提取
  for (const reg of YT_REGEX_LIST) {
    const match = url.match(reg);
    if (match && match[1]) return match[1];
  }

  // 如果正则失败，再用 URL API 兜底做一次解析
  try {
    const parsed = new URL(url);

    // https://youtu.be/<id>
    if (parsed.hostname === 'youtu.be' && parsed.pathname.length > 1) {
      const id = parsed.pathname.slice(1, 1 + ID_LEN);
      if (id.length === ID_LEN) return id;
    }

    // https://www.youtube.com/watch?v=<id>
    const vParam = parsed.searchParams.get('v');
    if (vParam && vParam.length === ID_LEN) return vParam;

    // 其它可能出现在 pathname 中的格式（如 /embed/, /v/, /live/, /shorts/）
    const pathParts = parsed.pathname.split('/');
    const keywords = ['embed', 'v', 'live', 'shorts'];
    const keyIndex = pathParts.findIndex(p => keywords.includes(p));
    if (keyIndex !== -1 && pathParts[keyIndex + 1]) {
      const id = pathParts[keyIndex + 1].slice(0, ID_LEN);
      if (id.length === ID_LEN) return id;
    }
  } catch {
    /* ignore malformed url */
  }

  return null;
};

const YoutubePlayer: React.FC<Props> = ({ url }) => {
  const videoId = getYoutubeId(url);

  if (!videoId) {
    return <p>Invalid YouTube link</p>;
  }

  return <YouTube videoId={videoId} className="youtube-iframe" />;
};

export default YoutubePlayer;

